import { useStore } from '../store';
import { useTranslation } from 'react-i18next';
import i18n from '../locales/i18n';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  X,
  ImagePlus,
  Sparkles,
  ChevronDown,
  Trash2,
  Check,
  Lock,
} from 'lucide-react';
import CameraCapture from '../components/CameraCapture';
import { useTelegram } from '../hooks/useTelegram';
import { useAuthStore } from '../store/authStore';
import {
  useProfileQuery,
  useSubscriptionCurrentQuery,
} from '../core/queries/account';
import {
  resolveActivePlanKey,
  resolveCreditsTotal,
} from '../core/accountPresentation';
import { usePromptPresetsQuery } from '../core/queries/presets';
import {
  useDeleteReferenceMutation,
  usePromptHelperMutation,
} from '../core/queries/mutations';
import { Icon } from '../components/primitives';
import { getCardTitleImageWidth } from '../components/catalog/titleImageSizing';
import CreateActionControls from '../components/create/CreateActionControls';
import { localizedField } from '../locales/content';
import {
  dispatchAppEvent,
  exitFullscreen,
  getPlatform,
  session,
} from '../platform';
import { parsePhotoPresetToken } from '../core/photoPresetId';
import { trackClientEvent } from '../analytics/track';
import useConsentGuard from '../hooks/useConsentGuard';
import { useConsentStore } from '../store/consentStore';
import { getPhotoCreditCost } from '../core/generationPricing';
import { hasPromptBatchAccess } from '../core/promptBatchAccess';
import BottomSheet from '../components/BottomSheet';
import ReferencePickerFields from '../components/catalog/ReferencePickerFields';

/**
 * Prompt — экран `/prompt/:presetId`.
 *
 * Figma source: `318:5080` prompt-image (402×809), captured via figma-console MCP.
 *
 * Структура (Figma):
 *  - header (full-bleed 320h) — обложка пресета с вертикальным fade-градиентом
 *    (transparent → surface/1 lvl). НЕТ overlay-бейджа «Изменить», НЕТ overlay
 *    заголовка — figma-data показывает только image + gradient, текст «PINK
 *    BOXING» в Figma-мок-боку запечён в imageRef. Возврат — через Telegram
 *    BackButton (statusbar action=back per figma `318:5102`).
 *  - form (paddingX 16, gap 16, vertical):
 *    - attachments — кнопка «Выбрать изображение / сделать фото» с
 *      orange-gradient рамкой (stroke gradient/start → gradient/end), corner 16.
 *    - textArea — gradient-fill карточка corner 16: textarea + char-counter
 *      «N / 1000» (top-right area, opacity 0.48) + AI-кнопка «Сгенерировать
 *      промт» (bottom-left, surface/5 lvl, corner 20, backdrop-blur).
 *    - row — два dropdown'а 16:9 / 2K (corner 16, gradient stroke).
 *  - bottom-panel (fixed, surface/1 lvl, gap 24, paddingY 16/24):
 *    - counter pill [−] N/4 [+] (3 tag'а в surface/5 lvl pill).
 *    - ctaButton — orange gradient, «Создать (N кредита)» + «Баланс: N 🪙».
 *
 * Удалено по Figma: блок «Модель: Nano Banana Pro», заголовок «Промпт»
 * над textarea, список «Примеры промптов» (заменены AI-кнопкой
 * «Сгенерировать промт» внутри textarea).
 */

const ASPECT_RATIOS = [
  { id: '9:16', label: '9:16' },
  { id: '3:4', label: '3:4' },
  { id: '2:3', label: '2:3' },
  { id: '1:1', label: '1:1' },
  { id: '4:3', label: '4:3' },
  { id: '16:9', label: '16:9' },
  { id: '3:2', label: '3:2' },
];

const PROMPT_TEXT_ENTRY_CLASS = 'prompt-text-entry-active';

function isMobileKeyboardDevice() {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator?.userAgent || '';
  const telegramPlatform = getPlatform();
  return (
    /Android|iPhone|iPad|iPod/i.test(userAgent) ||
    /android|ios|iphone|ipad/i.test(telegramPlatform) ||
    window.matchMedia?.('(pointer: coarse)')?.matches
  );
}

function isKeyboardLayoutCandidate() {
  if (typeof window === 'undefined') return false;
  const viewportWidth = window.visualViewport?.width || window.innerWidth || 0;
  return isMobileKeyboardDevice() || viewportWidth <= 900;
}

function isIOSKeyboardDevice() {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator?.userAgent || '';
  const telegramPlatform = getPlatform();
  return /iPhone|iPad|iPod/i.test(userAgent) || /ios|iphone|ipad/i.test(telegramPlatform);
}

/** Quality icon — Figma 571:8649 (type=image). Image-frame with mountain. */
function QualityIcon({ className = '' }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={`text-white ${className}`}
    >
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="9" cy="11" r="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3 17l5-5 4 4 3-3 6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Inline aspect-ratio rectangle glyph — proportional w×h with rounded corners. */
function AspectGlyph({ id }) {
  const sizeMap = {
    '9:16': [11, 18],
    '3:4': [13, 17],
    '2:3': [13, 19],
    '1:1': [16, 16],
    '4:3': [18, 13],
    '16:9': [20, 11],
    '3:2': [19, 13],
  };
  const [w, h] = sizeMap[id] || [16, 16];
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white">
      <rect
        x={(24 - w) / 2}
        y={(24 - h) / 2}
        width={w}
        height={h}
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

const getQualityOptions = (plan, t) => {
  const premiumAccess = plan === 'PREMIUM' || plan === 'ULTRA';

  return [
    {
      id: '1.5K',
      label: '1K',
      description: t('prompt.resolution15k'),
      disabled: false,
    },
    {
      id: '2K',
      label: '2K',
      description: premiumAccess ? t('prompt.resolution2k') : t('prompt.resolution2kLocked'),
      disabled: !premiumAccess,
      badge: premiumAccess ? null : t('prompt.premiumBadge'),
    },
  ];
};

const MAX_REFERENCES = 8;
const MAX_PROMPT_LENGTH = 1000;

/** Russian credit pluralization: 1 → кредит, 2-4 → кредита, 5+ → кредитов. */
function pluralCreditsKey(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'credits5';
  if (mod10 === 1) return 'credit';
  if (mod10 >= 2 && mod10 <= 4) return 'credits234';
  return 'credits5';
}

const HERO_PRESET_PRIORITY = [
  'boks2_mn8oe2fo',
  'paren_mls8kmc6',
  'devushka_mlrumi6i',
  'tennis3_mn8o5t71',
  'boks_mn8odg8b',
  'woman_mm579h59',
];

const HERO_FALLBACK_PRESETS = [
  {
    id: 'devushka_mlrumi6i',
    name: 'Закат на крыше',
    nameEn: 'Rooftop sunset',
    image: '/img_presets/devushka_mlrumi6i.webp',
  },
  {
    id: 'valentinesday_mli3hb30',
    name: 'Valentine editorial',
    nameEn: 'Valentine editorial',
    image: '/img_presets/valentinesday_mli3hb30.webp',
  },
  {
    id: 'paren_mls8kmc6',
    name: 'Стритстайл',
    nameEn: 'Street style',
    image: '/img_presets/paren_mls8kmc6.webp',
  },
  {
    id: 'boks2_mn8oe2fo',
    name: 'Спорт энергия',
    nameEn: 'Sport energy',
    image: '/img_presets/boks2_mn8oe2fo.webp',
  },
  {
    id: 'tennis3_mn8o5t71',
    name: 'Теннис',
    nameEn: 'Tennis',
    image: '/img_presets/tennis3_mn8o5t71.webp',
  },
  {
    id: 'boks_mn8odg8b',
    name: 'Бокс',
    nameEn: 'Boxing',
    image: '/img_presets/boks_mn8odg8b.webp',
  },
  {
    id: 'woman_mm579h59',
    name: 'Портрет',
    nameEn: 'Portrait',
    image: '/img_presets/woman_mm579h59.webp',
  },
];

function buildHeroPresets(presets) {
  const isSinglePreset = (preset) => {
    const id = String(preset?.id || '');
    return id.includes('_') && !id.startsWith('grp_') && preset?.image && !preset?.presets;
  };
  const withImages = (presets || []).filter(isSinglePreset);
  if (withImages.length === 0) return HERO_FALLBACK_PRESETS;

  const byId = new Map(withImages.map((p) => [p.id, p]));
  const priority = HERO_PRESET_PRIORITY.map((id) => byId.get(id)).filter(Boolean);
  const merged = [...priority, ...withImages, ...HERO_FALLBACK_PRESETS].filter(Boolean);
  const seen = new Set();
  return merged.filter((preset) => {
    if (seen.has(preset.id)) return false;
    seen.add(preset.id);
    return true;
  }).slice(0, 10);
}

function getPresetTitleImage(preset) {
  const titleImage = preset?.groupTitleImage || preset?.groupLogoUrl || null;
  return typeof titleImage === 'string' && titleImage.trim() ? titleImage : null;
}

function getPromptCarouselVirtualIndex(presets, selectedPresetId, centerBlock) {
  if (!presets.length) return 0;
  const selectedIndex = selectedPresetId
    ? presets.findIndex((preset) => preset.id === selectedPresetId)
    : -1;
  return centerBlock * presets.length + (selectedIndex >= 0 ? selectedIndex : 0);
}

function PromptPresetCarousel({
  presets,
  selectedPresetId,
  onSelectPreset,
  onClearPreset,
}) {
  const itemWidth = 158;
  const swipeThreshold = 42;
  const repeatBlocks = 15;
  const centerBlock = Math.floor(repeatBlocks / 2);
  const renderWindow = 2;
  const [activeVirtualIndex, setActiveVirtualIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [skipTransition, setSkipTransition] = useState(false);
  const previousSelectedIdRef = useRef(selectedPresetId);
  const selectedVirtualIndexRef = useRef(null);
  const touchStartRef = useRef(null);
  const suppressClickRef = useRef(false);
  const presetCount = presets.length;
  const activeIndex =
    presetCount > 0 ? ((activeVirtualIndex % presetCount) + presetCount) % presetCount : 0;
  const loopedPresets = useMemo(
    () =>
      presets.length > 0
        ? Array.from({ length: repeatBlocks }, () => presets).flat()
        : [],
    [presets, repeatBlocks]
  );
  const selectedRenderVirtualIndex = useMemo(
    () => getPromptCarouselVirtualIndex(presets, selectedPresetId, centerBlock),
    [centerBlock, presets, selectedPresetId]
  );
  const renderVirtualIndex =
    selectedPresetId &&
    presetCount > 0 &&
    loopedPresets[activeVirtualIndex]?.id !== selectedPresetId
      ? selectedRenderVirtualIndex
      : activeVirtualIndex;

  useEffect(() => {
    setDragOffset(0);
    setIsDragging(false);
    if (presetCount === 0) {
      setActiveVirtualIndex(0);
      return;
    }
    const selectedIndex = presets.findIndex((p) => p.id === selectedPresetId);
    const selectedVirtualIndex = selectedVirtualIndexRef.current;
    const canKeepSelectedVirtualIndex =
      selectedIndex >= 0 &&
      selectedVirtualIndex != null &&
      loopedPresets[selectedVirtualIndex]?.id === selectedPresetId;
    if (canKeepSelectedVirtualIndex) {
      setSkipTransition(false);
      setActiveVirtualIndex(selectedVirtualIndex);
      selectedVirtualIndexRef.current = null;
      return;
    }
    setSkipTransition(true);
    setActiveVirtualIndex((current) => {
      const normalized = ((current % presetCount) + presetCount) % presetCount;
      return centerBlock * presetCount + (selectedIndex >= 0 ? selectedIndex : normalized);
    });
    selectedVirtualIndexRef.current = null;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setSkipTransition(false));
    });
  }, [centerBlock, loopedPresets, presets, presetCount, selectedPresetId]);

  useEffect(() => {
    if (selectedPresetId || isDragging || presetCount <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveVirtualIndex((index) => index + 1);
    }, 2800);
    return () => window.clearInterval(timer);
  }, [isDragging, presetCount, selectedPresetId]);

  useEffect(() => {
    const previousSelectedId = previousSelectedIdRef.current;
    previousSelectedIdRef.current = selectedPresetId;

    if (!previousSelectedId || selectedPresetId || presetCount <= 1) return undefined;

    const timer = window.setTimeout(() => {
      setActiveVirtualIndex((index) => index + 1);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [presetCount, selectedPresetId]);

  const settleLoopPosition = () => {
    if (selectedPresetId || presetCount <= 1) return;
    if (
      activeVirtualIndex < presetCount * 2 ||
      activeVirtualIndex >= presetCount * (repeatBlocks - 2)
    ) {
      setSkipTransition(true);
      setActiveVirtualIndex(centerBlock * presetCount + activeIndex);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setSkipTransition(false));
      });
    }
  };

  const finishSwipe = (direction) => {
    if (!direction || presetCount <= 1) return;
    if (selectedPresetId) {
      onClearPreset();
      return;
    }
    setActiveVirtualIndex((index) => index + direction);
  };

  const resetTouch = () => {
    touchStartRef.current = null;
    setDragOffset(0);
    setIsDragging(false);
  };

  const handleTouchStart = (event) => {
    if (presetCount <= 1) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleTouchMove = (event) => {
    const start = touchStartRef.current;
    const touch = event.touches?.[0];
    if (!start || !touch) return;
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 8 || Math.abs(deltaX) < Math.abs(deltaY)) return;
    setIsDragging(true);
    setDragOffset(Math.max(-itemWidth, Math.min(itemWidth, deltaX)));
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    const touch = event.changedTouches?.[0];
    if (!start || !touch) {
      resetTouch();
      return;
    }
    const deltaX = touch.clientX - start.x;
    const dragged = Math.abs(deltaX) >= swipeThreshold;
    if (isDragging || dragged) {
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 220);
    }
    resetTouch();
    if (!dragged) return;
    finishSwipe(deltaX < 0 ? 1 : -1);
  };

  return (
    <section
      className="relative overflow-hidden pb-2"
      style={{
        paddingTop: 'calc(53px - var(--page-content-top-shift, 0px))',
      }}
    >
      <div aria-hidden="true" className="relative z-10 mb-2 h-7 px-4" />

      <div
        className="relative h-[218px] overflow-hidden"
        data-testid="prompt-preset-carousel"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={resetTouch}
        style={{ touchAction: 'pan-y' }}
      >
        <div
          className={`flex gap-3 ease-out will-change-transform ${
            skipTransition || isDragging ? '' : 'transition-transform duration-700'
          }`}
          style={{
            transform: `translateX(calc(50% - 73px - ${renderVirtualIndex * itemWidth}px + ${dragOffset}px))`,
          }}
          onTransitionEnd={settleLoopPosition}
        >
          {loopedPresets.map((preset, index) => {
            const name = localizedField(preset, 'name') || preset.name || '';
            const titleImage = getPresetTitleImage(preset);
            const isActive = index === renderVirtualIndex;
            const isSelected = isActive && selectedPresetId && preset.id === selectedPresetId;
            const shouldRenderMedia = Math.abs(index - renderVirtualIndex) <= renderWindow;
            if (!shouldRenderMedia) {
              return (
                <div
                  key={`${preset.id}-${index}`}
                  aria-hidden="true"
                  className="mt-4 h-[192px] w-[146px] shrink-0"
                />
              );
            }
            return (
              <button
                key={`${preset.id}-${index}`}
                type="button"
                onClick={(event) => {
                  if (suppressClickRef.current) {
                    event.preventDefault();
                    return;
                  }
                  selectedVirtualIndexRef.current = index;
                  onSelectPreset(preset);
                }}
                className={`relative mt-4 h-[192px] w-[146px] shrink-0 overflow-visible rounded-2xl border text-left transition-all duration-700 ${
                  isSelected
                    ? 'scale-100 border-[#FFAC0B]'
                    : isActive
                      ? 'scale-100 border-transparent'
                      : 'scale-[0.88] border-transparent opacity-60'
                }`}
                aria-label={name}
              >
                <img
                  src={preset.image}
                  alt={name}
                  className="absolute inset-0 h-full w-full rounded-[inherit] object-cover"
                  loading={isActive ? 'eager' : 'lazy'}
                  decoding="async"
                />
                <div
                  className="absolute inset-0 rounded-[inherit]"
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(1,1,3,0) 25%, rgba(1,1,3,0.34) 58%, rgba(1,1,3,0.92) 100%)',
                  }}
                />
                {isSelected && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Сбросить пресет"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClearPreset();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onClearPreset();
                      }
                    }}
                    className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white shadow-[0_0_14px_rgba(0,0,0,0.35)] backdrop-blur-md"
                  >
                    <X className="h-4 w-4" />
                  </span>
                )}
                {titleImage ? (
                  <img
                    src={titleImage}
                    alt=""
                    className="absolute left-1/2 bottom-4 z-[2] h-auto max-w-full -translate-x-1/2 object-contain pointer-events-none"
                    style={{ width: getCardTitleImageWidth('M') }}
                    loading={isActive ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                  />
                ) : (
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <p className="line-clamp-2 text-sm font-bold leading-4 text-white">
                      {name}
                    </p>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PromptSkeleton() {
  const shimmerStyle = {
    '--skeleton-base': '#151517',
  };
  const fieldStyle = {
    '--skeleton-base': 'rgba(255,255,255,0.08)',
  };

  return (
    <div
      className="min-h-screen bg-[#010103] text-white"
      data-testid="prompt-skeleton"
      style={{
        paddingTop: 'var(--safe-top, 0px)',
        paddingBottom: '120px',
      }}
    >
      <section
        className="relative overflow-hidden pb-2"
        style={{
          paddingTop: 'calc(53px - var(--page-content-top-shift, 0px))',
        }}
      >
        <div aria-hidden="true" className="relative z-10 mb-2 h-7 px-4" />
        <div className="relative h-[218px] overflow-hidden">
          <div className="flex justify-center gap-3 px-4">
            <div
              className="mt-4 h-[192px] w-[146px] shrink-0 rounded-2xl opacity-60 skeleton-shimmer"
              style={shimmerStyle}
            />
            <div
              className="relative mt-4 h-[192px] w-[146px] shrink-0 rounded-2xl skeleton-shimmer"
              style={shimmerStyle}
              data-role="prompt-skeleton-hero"
            >
              <div
                className="absolute inset-x-0 bottom-3 mx-3 h-4 rounded-md"
                style={{ ...shimmerStyle, width: '60%' }}
              />
              <div
                className="absolute inset-x-0 bottom-9 mx-3 h-3 rounded-md opacity-70"
                style={{ ...shimmerStyle, width: '38%' }}
              />
            </div>
            <div
              className="mt-4 h-[192px] w-[146px] shrink-0 rounded-2xl opacity-60 skeleton-shimmer"
              style={shimmerStyle}
            />
          </div>
        </div>
      </section>

      <div className="px-4 pt-0 flex flex-col gap-4" data-role="prompt-skeleton-form">
        <div
          className="relative h-[60px] w-full rounded-2xl skeleton-shimmer"
          style={fieldStyle}
          data-role="prompt-skeleton-attachments"
        >
          <div
            className="absolute left-1/2 top-1/2 h-5 w-40 -translate-x-1/2 -translate-y-1/2 rounded-md"
            style={shimmerStyle}
          />
        </div>

        <div
          className="relative h-40 w-full rounded-2xl p-4 skeleton-shimmer"
          style={fieldStyle}
          data-role="prompt-skeleton-textarea"
        >
          <div className="flex flex-col gap-3">
            <div className="h-4 w-[78%] rounded-md" style={shimmerStyle} />
            <div className="h-4 w-[92%] rounded-md" style={shimmerStyle} />
            <div className="h-4 w-[64%] rounded-md" style={shimmerStyle} />
          </div>
          <div
            className="absolute bottom-3 left-3 h-8 w-32 rounded-2xl"
            style={shimmerStyle}
          />
          <div
            className="absolute bottom-4 right-4 h-3 w-14 rounded-md opacity-70"
            style={shimmerStyle}
          />
        </div>

        <div className="grid grid-cols-2 gap-3" data-role="prompt-skeleton-dropdowns">
          <div
            className="h-12 rounded-2xl skeleton-shimmer"
            style={fieldStyle}
          />
          <div
            className="h-12 rounded-2xl skeleton-shimmer"
            style={fieldStyle}
          />
        </div>
      </div>

      <div
        className="pointer-events-none fixed left-0 right-0 z-30 px-4"
        style={{ bottom: 'calc(var(--safe-bottom, 0px) + 76px)' }}
        data-role="prompt-skeleton-cta"
      >
        <div className="flex items-stretch gap-3">
          <div
            className="flex h-[60px] w-[118px] items-center gap-2 rounded-2xl p-0.5 skeleton-shimmer"
            style={{ '--skeleton-base': 'rgba(255,255,255,0.08)' }}
            data-role="prompt-skeleton-counter"
          >
            <div className="h-full w-10 rounded-2xl skeleton-shimmer" style={{ '--skeleton-base': 'rgba(255,255,255,0.12)' }} />
            <div className="h-5 w-8 rounded-md skeleton-shimmer" style={{ '--skeleton-base': 'rgba(255,255,255,0.12)' }} />
            <div className="h-full w-10 rounded-2xl skeleton-shimmer" style={{ '--skeleton-base': 'rgba(255,255,255,0.12)' }} />
          </div>
          <div
            className="flex h-[60px] flex-1 flex-col items-center justify-center rounded-2xl skeleton-shimmer"
            style={{ '--skeleton-base': 'rgba(255,194,76,0.72)' }}
          >
            <div className="h-4 w-40 rounded-md bg-black/15" />
            <div className="mt-1.5 h-3 w-24 rounded-md bg-black/10" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Prompt() {
  const navigate = useNavigate();
  const { presetId: presetToken } = useParams();
  const routePreset = parsePhotoPresetToken(presetToken);
  const selectedPresetId = routePreset.presetId !== 'general' ? routePreset.presetId : '';
  const effectivePresetId = selectedPresetId || 'general';
  const { t } = useTranslation();
  const { hapticFeedback, showAlert } = useTelegram();
  const { requireConsent } = useConsentGuard();
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const authStoreState = useAuthStore();
  const { user } = authStoreState;
  const profileQuery = useProfileQuery({
    retry: false,
    enabled: !consentRequired,
  });
  const subscriptionQuery = useSubscriptionCurrentQuery({
    retry: false,
    enabled: !consentRequired,
  });
  const generateImage = useStore((state) => state.generateImage);
  const isGenerating = useStore((state) => state.isGenerating);
  const references = useStore((state) => state.promptDraftReferences);
  const setReferences = useStore((state) => state.setPromptDraftReferences);
  const uploading = useStore((state) => state.promptDraftUploading);
  const uploadReferences = useStore((state) => state.uploadPromptDraftReferences);
  const createInFlightRef = useRef(false);
  const promptBoxRef = useRef(null);
  const promptTextareaRef = useRef(null);
  const promptControlsRef = useRef(null);
  const promptKeyboardActiveRef = useRef(false);
  const promptFocusStartedAtRef = useRef(0);
  const keyboardInsetRef = useRef(0);
  const keyboardViewportBaselineRef = useRef(0);

  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [quality, setQuality] = useState('1.5K');
  const [batchCount, setBatchCount] = useState(1);
  const [showAspectDropdown, setShowAspectDropdown] = useState(false);
  const [showQualityDropdown, setShowQualityDropdown] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [, setCurrentGroupId] = useState('');
  const [, setGroupName] = useState('');
  const [inputError, setInputError] = useState(false);
  const [gatewayError, setGatewayError] = useState(false);
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const [keyboardInset, setKeyboardInset] = useState(0);
  // AI prompt-helper state (handoff §8). aiUnavailable is sticky for the
  // current page mount — once the backend reports 503 we keep the button
  // disabled with «Сейчас недоступно» until the user navigates away.
  const [aiUnavailable, setAiUnavailable] = useState(false);
  const deleteReferenceMutation = useDeleteReferenceMutation();
  const promptHelperMutation = usePromptHelperMutation();
  const aiLoading = promptHelperMutation.isPending;

  const usage = authStoreState?.usage || { daily: 0, daily_limit: 10 };
  const credits = authStoreState?.credits || { total: 0, extra: 0, monthly: 0 };
  const hasInitialized = authStoreState?.hasInitialized;
  const presetsQuery = usePromptPresetsQuery(effectivePresetId, {
    enabled: Boolean(hasInitialized),
  });
  const presets = presetsQuery.data?.presets || [];
  const promptCarouselPresets = presetsQuery.data?.promptCarouselPresets || [];
  const selectedPreset = presetsQuery.data?.selectedPreset || null;

  useEffect(() => {
    if (!hasInitialized || presetsQuery.isLoading) return;
    trackClientEvent('preset_opened', {
      presetId: effectivePresetId,
      groupId: session.getItem('currentGroupId') || null,
    });
  }, [effectivePresetId, hasInitialized, presetsQuery.isLoading]);

  const setPromptTextEntryMode = (active) => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle(PROMPT_TEXT_ENTRY_CLASS, active);
    document.documentElement.classList.toggle(PROMPT_TEXT_ENTRY_CLASS, active);
  };

  const setPromptKeyboardMode = (active) => {
    promptKeyboardActiveRef.current = active;
    setPromptTextEntryMode(active);
    setIsPromptFocused(active);
  };

  const revealPromptBoxForKeyboard = () => {
    if (!isKeyboardLayoutCandidate()) return;
    const node = promptControlsRef.current || promptBoxRef.current;
    if (!node || typeof window === 'undefined') return;
    const viewport = window.visualViewport;
    const viewportHeight = viewport?.height || window.innerHeight || 0;
    const baselineHeight = keyboardViewportBaselineRef.current || Math.round(window.innerHeight || 0);
    const viewportKeyboardDelta = Math.max(0, baselineHeight - Math.round(viewportHeight));
    const needsIOSFallback =
      isIOSKeyboardDevice() && keyboardInset < 80 && viewportKeyboardDelta < 120;
    const keyboardFallback = needsIOSFallback ? 220 : 0;
    const createPanelReserve = 116;
    const viewportBottom = viewport
      ? viewport.offsetTop + viewport.height - keyboardFallback
      : window.innerHeight - keyboardFallback;
    const targetBottom = viewportBottom - createPanelReserve - 12;
    const rect = node.getBoundingClientRect();
    if (rect.bottom > targetBottom) {
      window.scrollBy({ top: rect.bottom - targetBottom, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (routePreset.quality) {
      setQuality(routePreset.quality === '4K' ? '2K' : routePreset.quality);
    }
  }, [routePreset.quality]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return undefined;

    const updateKeyboardInset = () => {
      const viewport = window.visualViewport;
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      const roundedInset = Math.round(inset);
      const viewportHeight = Math.round(viewport.height);
      const previousInset = keyboardInsetRef.current;
      keyboardInsetRef.current = roundedInset;
      setKeyboardInset(roundedInset);

      if (!promptKeyboardActiveRef.current && viewportHeight > keyboardViewportBaselineRef.current) {
        keyboardViewportBaselineRef.current = viewportHeight;
      }

      if (!isIOSKeyboardDevice()) {
        const promptHasFocus = document.activeElement === promptTextareaRef.current;
        const baselineHeight = keyboardViewportBaselineRef.current || viewportHeight;
        const heightDelta = baselineHeight - viewportHeight;
        const keyboardVisible = roundedInset >= 40 || heightDelta >= 120;
        const openedAndClosed = previousInset >= 40;
        const justFocused = Date.now() - promptFocusStartedAtRef.current < 900;
        if (promptHasFocus && keyboardVisible && !promptKeyboardActiveRef.current) {
          setPromptKeyboardMode(true);
          window.setTimeout(revealPromptBoxForKeyboard, 80);
          window.setTimeout(revealPromptBoxForKeyboard, 260);
        } else if (!keyboardVisible && (openedAndClosed || promptKeyboardActiveRef.current) && !justFocused) {
          setPromptKeyboardMode(false);
        } else if (!promptHasFocus && !justFocused) {
          setPromptKeyboardMode(false);
        }
      }
    };

    updateKeyboardInset();
    window.visualViewport.addEventListener('resize', updateKeyboardInset);
    window.visualViewport.addEventListener('scroll', updateKeyboardInset);
    return () => {
      window.visualViewport.removeEventListener('resize', updateKeyboardInset);
      window.visualViewport.removeEventListener('scroll', updateKeyboardInset);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (typeof document === 'undefined') return;
      setPromptTextEntryMode(false);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const clearPromptTextEntryMode = () => {
      setPromptKeyboardMode(false);
    };

    document.addEventListener('visibilitychange', clearPromptTextEntryMode);
    window.addEventListener('pageshow', clearPromptTextEntryMode);
    window.addEventListener('blur', clearPromptTextEntryMode);
    return () => {
      document.removeEventListener('visibilitychange', clearPromptTextEntryMode);
      window.removeEventListener('pageshow', clearPromptTextEntryMode);
      window.removeEventListener('blur', clearPromptTextEntryMode);
    };
  }, []);

  // Чтение данных для повтора генерации
  useEffect(() => {
    const storedGroupName = session.getItem('currentGroupName');
    if (storedGroupName) setGroupName(storedGroupName);
    const storedGroupId = session.getItem('currentGroupId');
    if (storedGroupId) setCurrentGroupId(storedGroupId);
    const repeatPrompt = session.getItem('repeatPrompt');
    const repeatAspectRatio = session.getItem('repeatAspectRatio');
    if (repeatPrompt) {
      setPrompt(repeatPrompt);
      session.removeItem('repeatPrompt');
    }
    if (repeatAspectRatio) {
      setAspectRatio(repeatAspectRatio);
      session.removeItem('repeatAspectRatio');
    }
    const repeatQuality = session.getItem('repeatQuality');
    if (repeatQuality) {
      setQuality(repeatQuality === '4K' ? '2K' : repeatQuality);
      session.removeItem('repeatQuality');
    }
    const repeatReference = session.getItem('repeatReference');
    if (repeatReference) {
      try {
        const refs = JSON.parse(repeatReference);
        if (Array.isArray(refs)) {
          setReferences(refs.map((url, i) => ({ id: Date.now() + i, preview: url, url })));
        } else {
          setReferences([{ id: Date.now(), preview: repeatReference, url: repeatReference }]);
        }
      } catch {
        setReferences([{ id: Date.now(), preview: repeatReference, url: repeatReference }]);
      }
      session.removeItem('repeatReference');
    }
    const tempReferences = session.getItem('tempReferences');
    if (tempReferences) {
      try {
        const refs = JSON.parse(tempReferences);
        if (Array.isArray(refs)) {
          setReferences(refs.map((url, i) => ({ id: Date.now() + i, preview: url, url })));
        }
      } catch {
        /* ignore */
      }
      session.removeItem('tempReferences');
    }
    const tempPrompt = session.getItem('tempPrompt');
    if (tempPrompt) {
      setPrompt(tempPrompt);
      session.removeItem('tempPrompt');
    }
    const tempAspectRatio = session.getItem('tempAspectRatio');
    if (tempAspectRatio) {
      setAspectRatio(tempAspectRatio);
      session.removeItem('tempAspectRatio');
    }
    const tempQuality = session.getItem('tempQuality');
    if (tempQuality) {
      setQuality(tempQuality === '4K' ? '2K' : tempQuality);
      session.removeItem('tempQuality');
    }
  }, []);

  const processSelectedFiles = async (files) => {
    if (files.length === 0) return;

    setInputError(false);
    const remainingSlots = MAX_REFERENCES - references.length;
    const filesToProcess = files.slice(0, remainingSlots);

    if (filesToProcess.length === 0) {
      showAlert(t('prompt.maxRefs', { max: MAX_REFERENCES }));
      return;
    }

    setShowImagePicker(false);
    hapticFeedback('light');

    try {
      await uploadReferences(filesToProcess);
      hapticFeedback('success');
    } catch (error) {
      console.error('Failed to upload images:', error);
      showAlert(t('prompt.uploadError'));
    }
  };

  const removeReference = (id) => {
    hapticFeedback('light');
    const ref = references.find((r) => r.id === id);
    if (ref?.url) {
      deleteReferenceMutation.mutate(ref.url);
    }
    setReferences((prev) => prev.filter((r) => r.id !== id));
  };

  const openImagePicker = () => {
    requireConsent(() => {
      hapticFeedback('light');
      setShowImagePicker(true);
    }, 'upload');
  };

  const takePhoto = () => {
    setShowImagePicker(false);
    try {
      exitFullscreen();
    } catch (e) {
      /* ignore */
    }
    setShowCamera(true);
  };

  /** AI-кнопка «Сгенерировать промт» (handoff §8).
   *  Calls POST /api/prompt-helper. On 503 (no OPENAI_API_KEY or upstream
   *  outage) the backend returns { error: 'ai_unavailable' }; we flip the
   *  button into a disabled «Сейчас недоступно» state per handoff. */
  const performAIPromptAssist = async () => {
    if (aiLoading || aiUnavailable) return;
    hapticFeedback('light');
    try {
      const res = await promptHelperMutation.mutateAsync({
        userText: prompt,
        presetId: effectivePresetId,
        language: i18n.language?.startsWith('en') ? 'en' : 'ru',
      });
      const generated = res?.prompt;
      if (typeof generated === 'string' && generated.trim().length > 0) {
        setPrompt(generated.slice(0, MAX_PROMPT_LENGTH));
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        setAiUnavailable(true);
      } else if (status === 429) {
        showAlert(t('prompt.aiRateLimited'));
      } else {
        showAlert(t('prompt.aiError'));
      }
    }
  };

  const handleAIPromptAssist = () => {
    requireConsent(performAIPromptAssist, 'prompt_helper');
  };

  const presetData =
    selectedPreset || {
      id: 'general',
      name: 'GENERAL',
      nameEn: 'GENERAL',
      credits: 2,
      image: '',
    };
  const heroPresets = useMemo(
    () =>
      buildHeroPresets(
        promptCarouselPresets.length > 0 ? promptCarouselPresets : presets
      ),
    [promptCarouselPresets, presets]
  );
  const selectedHeroPreset = selectedPresetId
    ? heroPresets.find((preset) => preset.id === selectedPresetId)
    : null;
  const selectedPresetName =
    selectedPresetId && (localizedField(selectedHeroPreset || presetData, 'name') || presetData.name);
  const promptPlaceholder = selectedPresetId
    ? t('prompt.selectedModePlaceholder', { name: selectedPresetName })
    : t('prompt.freeModePlaceholder');
  const profileUser = profileQuery.data?.user ?? profileQuery.data ?? null;
  const accountLoading = profileQuery.isLoading && !profileUser;
  const accountUser = profileUser ?? user ?? null;
  const accountPlanKey = resolveActivePlanKey({
    subscription: subscriptionQuery.data?.subscription ?? null,
    user: accountUser,
  });
  const qualityOptions = useMemo(
    () => getQualityOptions(accountPlanKey, t),
    [accountPlanKey, t]
  );
  const accountCreditsTotal = resolveCreditsTotal({
    credits: profileUser ? null : credits,
    user: accountUser,
  });
  const accountCreditsExtra = Number(profileUser?.creditsExtra ?? credits?.extra ?? 0) || 0;
  const selectedAspect = ASPECT_RATIOS.find((a) => a.id === aspectRatio);
  const selectedQualityOption = qualityOptions.find((q) => q.id === quality) || qualityOptions[0];
  const totalCredits = getPhotoCreditCost(presetData.credits, quality) * batchCount;
  const totalCreditsWord = t(`prompt.${pluralCreditsKey(totalCredits)}`);
  const balanceValue = accountCreditsTotal;

  useEffect(() => {
    if (!qualityOptions.some((option) => option.id === quality && !option.disabled)) {
      setQuality('1.5K');
    }
  }, [quality, qualityOptions]);

  if (!hasInitialized || presetsQuery.isLoading || authStoreState.isLoading || accountLoading) {
    return <PromptSkeleton />;
  }

  if (presetsQuery.isError) {
    return (
      <div
        className="min-h-screen bg-[#010103] flex flex-col items-center justify-center gap-4 px-6 text-center text-white"
        role="alert"
        style={{ paddingTop: 'var(--safe-top, 0px)' }}
      >
        <p className="text-sm text-white/70">
          {t('home.loadError', 'Failed to load data')}
        </p>
        <button
          type="button"
          onClick={() => presetsQuery.refetch()}
          className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-black"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    );
  }

  const performGenerate = async () => {
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;
    try {
      window.Telegram?.WebApp?.hideKeyboard?.();
    } catch (error) {
      /* ignore */
    }
    if (document.activeElement === promptTextareaRef.current) {
      promptTextareaRef.current?.blur();
    }
    setPromptKeyboardMode(false);

    navigate('/gallery');

    try {
      await generateImage(
        effectivePresetId,
        prompt.trim(),
        aspectRatio,
        quality,
        batchCount,
        references
      );
    } catch (err) {
      const status = err?.response?.status;
      const rawMessage = String(
        err?.response?.data?.error || err?.response?.data?.message || err?.message || ''
      );
      const lower = rawMessage.toLowerCase();
      const transientGatewayError =
        [502, 503, 504, 524].includes(status) ||
        lower.includes('timeout') ||
        lower.includes('gateway');
      if (transientGatewayError) {
        setGatewayError(true);
        return;
      }
      showAlert(err.response?.data?.message || t('prompt.genError'));
    } finally {
      createInFlightRef.current = false;
    }
  };

  const handleGenerate = () => {
    if (createInFlightRef.current) return;
    setGatewayError(false);
    if (!prompt.trim() && references.length === 0) {
      setInputError(true);
      return;
    }
    const hasPurchasedCredits = accountCreditsExtra > 0;
    if (
      !hasPurchasedCredits &&
      accountPlanKey !== 'ULTRA' &&
      usage &&
      usage.daily >= usage.daily_limit
    ) {
      dispatchAppEvent('app:insufficient-access', { message: t('prompt.dailyLimit') });
      return;
    }

    requireConsent(performGenerate, 'generate');
  };

  const navigateToPremiumPlans = () => {
    navigate('/plans', { state: { paywallTrigger: 'premium_model' } });
  };

  const openPremiumPlans = () => {
    requireConsent(navigateToPremiumPlans, 'purchase');
  };

  const continueBatchIncrease = () => {
    const currentAccount = useAuthStore.getState();
    if (hasPromptBatchAccess(currentAccount)) {
      setBatchCount((count) => Math.min(4, count + 1));
      return;
    }
    navigateToPremiumPlans();
  };

  const handleIncBatch = () => {
    hapticFeedback('light');
    requireConsent(continueBatchIncrease, 'purchase');
  };

  const isPromptKeyboardActive = isPromptFocused;
  const promptViewportHeight =
    typeof window !== 'undefined'
      ? Math.round(window.visualViewport?.height || window.innerHeight || 0)
      : 0;
  const promptKeyboardHeightDelta =
    isPromptKeyboardActive && keyboardViewportBaselineRef.current
      ? Math.max(0, keyboardViewportBaselineRef.current - promptViewportHeight)
      : 0;
  const promptKeyboardFallback =
    isPromptKeyboardActive &&
    isIOSKeyboardDevice() &&
    keyboardInset < 80 &&
    promptKeyboardHeightDelta < 120
      ? 220
      : 0;
  const promptKeyboardSpace = isPromptKeyboardActive
    ? Math.max(keyboardInset, promptKeyboardFallback)
    : 0;
  const promptBottomPadding = isPromptKeyboardActive
    ? `calc(112px + ${promptKeyboardSpace}px)`
    : '120px';
  const promptControlsReserve = isPromptKeyboardActive ? 128 : 0;
  const promptCreatePanelBottom = isPromptKeyboardActive
    ? 'calc(var(--safe-bottom, 0px) + 8px)'
    : 'calc(var(--safe-bottom, 0px) + 76px)';

  return (
    <div
      className="min-h-screen bg-[#010103] text-white"
      style={{
        paddingTop: 'var(--safe-top, 0px)',
        paddingBottom: promptBottomPadding,
      }}
    >
      <PromptPresetCarousel
        presets={heroPresets}
        selectedPresetId={selectedPresetId}
        onSelectPreset={(preset) => {
          hapticFeedback('light');
          if (preset.id !== selectedPresetId) {
            setPrompt('');
            setInputError(false);
            setGatewayError(false);
          }
          navigate(`/prompt/${preset.id}`);
        }}
        onClearPreset={() => {
          hapticFeedback('light');
          navigate('/prompt');
        }}
      />

      {/* Form — Figma 318:5084 (paddingX 16, gap 16, paddingTop 0).
          Form starts immediately below hero — hero's bottom-fade gradient ends
          at surface/1 lvl, blending into form's surface/1 lvl bg. */}
      <div className="px-4 pt-0 flex flex-col gap-4">
        {/* Attach — Figma 571:9695 (h≈60, orange gradient stroke, corner 16). */}
        {references.length === 0 ? (
          <button
            onClick={openImagePicker}
            disabled={uploading}
            className="relative w-full h-[60px] rounded-2xl flex items-center justify-center gap-3 text-white/80 font-semibold text-sm overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,194,76,0.04), rgba(255,144,18,0.04))',
            }}
          >
            {/* Dashed gradient border per Figma 571:9695 / attachments.json.
                Default = orange gradient; inputError swaps both stops to
                rose to match Figma 40000026:13238 (validation state).
                CSS `border-style: dashed` несовместим с gradient stroke
                → рисуем dashed rect inline SVG как overlay (см.
                ReferenceSlot.jsx тот же паттерн). */}
            <svg
              aria-hidden="true"
              className="absolute inset-0 w-full h-full pointer-events-none"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="attach-btn-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={inputError ? '#F87171' : '#FFC24C'} />
                  <stop offset="100%" stopColor={inputError ? '#F87171' : '#FF9012'} />
                </linearGradient>
              </defs>
              <rect
                x="1"
                y="1"
                width="calc(100% - 2px)"
                height="calc(100% - 2px)"
                rx="15"
                ry="15"
                fill="none"
                stroke="url(#attach-btn-grad)"
                strokeWidth="1.5"
                strokeDasharray="6 4"
              />
            </svg>
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{t('common.loading')}</span>
              </>
            ) : (
              <>
                <ImagePlus className="w-5 h-5 text-white" />
                <span>{t('prompt.selectImageOrPhoto')}</span>
              </>
            )}
          </button>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {references.length < MAX_REFERENCES && (
                <button
                  onClick={openImagePicker}
                  disabled={uploading}
                  className="w-20 h-20 rounded-2xl border-2 border-dashed border-[#FFC24C] flex flex-col items-center justify-center gap-1 text-[#FFC24C]"
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <ImagePlus className="w-5 h-5" />
                      <span className="text-xs font-semibold">{t('prompt.more')}</span>
                    </>
                  )}
                </button>
              )}

              {references.map((ref) => (
                <div key={ref.id} className="relative w-20 h-20">
                  <img
                    src={ref.preview}
                    alt="Reference"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                  <button
                    onClick={() => removeReference(ref.id)}
                    className="absolute top-1 right-1 w-4 h-4 bg-white/[0.12] rounded-full flex items-center justify-center"
                  >
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ))}
            </div>
            {/* References caption — Figma 318:5272: «Добавьте до N референсов»
                / «Вы добавили максимум референсов». text/secondary 14. */}
            <p className="text-[#C9C9C9] text-sm">
              {references.length >= MAX_REFERENCES
                ? t('prompt.maxReferencesReached')
                : t('prompt.addUpToRefs', { max: MAX_REFERENCES })}
            </p>
          </div>
        )}

        {/* Textarea — Figma 571:9074 (h 160, gradient fill + stroke, corner 16).
            Char-counter top-right area + AI-кнопка «Сгенерировать промт» bottom-left. */}
        <div
          ref={promptBoxRef}
          className={`relative w-full h-40 rounded-2xl border p-4 ${inputError ? 'border-rose-500/60' : 'border-white/20'}`}
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(153,153,153,0.16) 100%)',
          }}
        >
          <textarea
            ref={promptTextareaRef}
            value={prompt}
            onChange={(e) => { setPrompt(e.target.value.slice(0, MAX_PROMPT_LENGTH)); setInputError(false); setGatewayError(false); }}
            onFocus={() => {
              const keyboardMode = isKeyboardLayoutCandidate();
              promptFocusStartedAtRef.current = Date.now();
              if (typeof window !== 'undefined') {
                const viewportHeight = window.visualViewport?.height || window.innerHeight || 0;
                keyboardViewportBaselineRef.current = Math.max(
                  keyboardViewportBaselineRef.current,
                  Math.round(viewportHeight)
                );
              }
              setPromptKeyboardMode(keyboardMode);
              if (keyboardMode) {
                window.setTimeout(revealPromptBoxForKeyboard, 120);
                window.setTimeout(revealPromptBoxForKeyboard, 360);
                window.setTimeout(revealPromptBoxForKeyboard, 700);
              }
            }}
            onBlur={() => {
              window.setTimeout(() => {
                setPromptKeyboardMode(false);
              }, 120);
            }}
            placeholder={promptPlaceholder}
            maxLength={MAX_PROMPT_LENGTH}
            className="w-full h-full bg-transparent text-white placeholder:text-[#C9C9C9] text-sm font-semibold focus:outline-none resize-none pb-10"
          />
          {/* AI button — Figma 571:9016 (surface/5 lvl, corner 20,
              text/secondary Bold 10). Bottom-left. Disabled when
              POST /api/prompt-helper reported 503 — handoff §8 specifies
              «Сейчас недоступно» copy for that state. */}
          <button
            onClick={handleAIPromptAssist}
            type="button"
            disabled={aiLoading || aiUnavailable}
            data-testid="ai-prompt-btn"
            data-ai-unavailable={aiUnavailable ? 'true' : 'false'}
            className={[
              'absolute left-3 bottom-3 px-3 py-1.5 rounded-2xl backdrop-blur-md',
              'flex items-center gap-1.5 font-bold text-[10px] tracking-tight',
              aiUnavailable
                ? 'bg-white/[0.04] text-[#C9C9C9]/[0.48] cursor-not-allowed'
                : 'bg-white/[0.08] text-[#C9C9C9]',
              aiLoading ? 'opacity-60' : '',
            ].join(' ')}
          >
            <Sparkles className="w-3 h-3 text-white" />
            <span>
              {aiUnavailable
                ? t('prompt.aiUnavailable')
                : t('prompt.generatePrompt')}
            </span>
          </button>
          {/* Trash + counter — Figma 318:5272 (filled state): bottom-right,
              trash icon clears prompt, counter «N / 1 000» text/secondary 0.48. */}
          <div className="absolute right-4 bottom-3 flex items-center gap-2">
            {prompt.length > 0 && (
              <button
                onClick={() => {
                  hapticFeedback('light');
                  setPrompt('');
                }}
                type="button"
                className="text-[#C9C9C9]/[0.48] hover:text-[#C9C9C9] transition-colors"
                aria-label={t('common.clear')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <span className="text-[#C9C9C9]/[0.48] text-xs font-semibold tracking-tight">
              {prompt.length} / 1 000
            </span>
          </div>
        </div>

        {/* Inline error caption — Figma 40000026:13238 places the hint
            directly under the textarea (left-aligned, rose), not under the
            bottom panel. Both errorRequired (validation) and errorGateway
            (transient 5xx) share the slot. */}
        {(inputError || gatewayError) && (
          <p className="text-rose-400 text-xs font-semibold -mt-1">
            {inputError ? t('prompt.errorRequired') : t('prompt.errorGateway')}
          </p>
        )}

        {/* Row of dropdowns — Figma 318:5092 (gap 12, two dropdowns 179×48).
            Active state — orange border per Figma 318:5219 (open dropdown). */}
        <div
          ref={promptControlsRef}
          className="grid grid-cols-2 gap-3 relative transition-[margin] duration-200 ease-out"
          style={{
            marginBottom: promptControlsReserve ? `${promptControlsReserve}px` : undefined,
          }}
        >
          <div className="relative">
            <button
              onClick={() => {
                setShowAspectDropdown(!showAspectDropdown);
                setShowQualityDropdown(false);
              }}
              className={`w-full h-12 rounded-2xl border px-4 flex items-center justify-between text-white transition-colors ${
                showAspectDropdown ? 'border-[#FFC24C]' : 'border-white/20'
              }`}
              style={{
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(153,153,153,0.16) 100%)',
              }}
            >
              <span className="flex items-center gap-3">
                <AspectGlyph id={selectedAspect?.id} />
                <span className="text-base font-semibold text-[#C9C9C9]">
                  {selectedAspect?.label}
                </span>
              </span>
              <ChevronDown
                className={`w-5 h-5 text-white transition-transform ${
                  showAspectDropdown ? 'rotate-180' : ''
                }`}
              />
            </button>
            {showAspectDropdown && (
              <div className="absolute left-0 bottom-full mb-2 w-[200px] bg-[#1f1f23] border border-white/10 rounded-2xl p-2 space-y-1 z-50">
                {ASPECT_RATIOS.map((ratio) => (
                  <button
                    key={ratio.id}
                    onClick={() => {
                      setAspectRatio(ratio.id);
                      setShowAspectDropdown(false);
                      hapticFeedback('light');
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm ${
                      aspectRatio === ratio.id
                        ? 'bg-white/10 text-white'
                        : 'text-[#C9C9C9] hover:bg-white/5'
                    }`}
                  >
                    <AspectGlyph id={ratio.id} />
                    <span className="font-semibold">{ratio.label}</span>
                    {aspectRatio === ratio.id && (
                      <span className="ml-auto text-white">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => {
                setShowQualityDropdown(!showQualityDropdown);
                setShowAspectDropdown(false);
              }}
              className={`w-full h-12 rounded-2xl border px-4 flex items-center justify-between text-white transition-colors ${
                showQualityDropdown ? 'border-[#FFC24C]' : 'border-white/20'
              }`}
              style={{
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(153,153,153,0.16) 100%)',
              }}
            >
              <span className="flex items-center gap-3">
                <QualityIcon />
                <span className="text-base font-semibold text-[#C9C9C9]">
                  {selectedQualityOption?.label}
                </span>
              </span>
              <ChevronDown
                className={`w-5 h-5 text-white transition-transform ${
                  showQualityDropdown ? 'rotate-180' : ''
                }`}
              />
            </button>
            {showQualityDropdown && (
              <div className="absolute right-0 bottom-full mb-2 w-[240px] bg-[#1f1f23] border border-white/10 rounded-2xl p-2 space-y-1 z-50">
                {qualityOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      if (opt.disabled) {
                        hapticFeedback('light');
                        openPremiumPlans();
                      } else {
                        setQuality(opt.id);
                      }
                      setShowQualityDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-3 rounded-xl flex items-start gap-3 ${
                      opt.disabled
                        ? 'opacity-70 hover:bg-white/5'
                        : quality === opt.id
                          ? 'bg-white/[0.06]'
                          : 'hover:bg-white/5'
                    }`}
                  >
                    <QualityIcon className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-bold text-base">{opt.label}</span>
                        {opt.disabled && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#FFAC0B]/20 px-2 py-0.5 text-[11px] font-bold text-[#FFAC0B]">
                            <Lock className="h-3 w-3" />
                            <span>{opt.badge}</span>
                          </span>
                        )}
                        {opt.credits > 1 && (
                          <span className="flex items-center gap-1 bg-[#FFAC0B]/20 text-[#FFAC0B] px-2 py-0.5 rounded-full text-xs font-bold">
                            <Icon name="coin" size={12} className="text-[#FFAC0B]" />
                            <span>{opt.credits}</span>
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#C9C9C9]/[0.64] mt-1">
                        {opt.description}
                      </p>
                    </div>
                    {quality === opt.id && !opt.disabled && (
                      <Check className="w-5 h-5 text-white shrink-0 mt-0.5" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Camera Capture */}
      {showCamera && (
        <CameraCapture
          onCapture={async (file) => {
            setShowCamera(false);
            await processSelectedFiles([file]);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}

      <BottomSheet
        open={showImagePicker}
        busy={uploading}
        title={t('videoGenerate.pickPhoto', 'Pick A Photo')}
        subtitle={t(
          'videoGenerate.pickPhotoHint',
          'Upload a high quality photo for better results',
        )}
        onClose={() => setShowImagePicker(false)}
        testId="prompt-photo-pick-sheet"
      >
        <ReferencePickerFields
          references={references}
          requiredCount={1}
          uploading={uploading}
          busy={uploading}
          selectedSource={null}
          onSourceSelect={(source) => {
            if (source === 'camera') takePhoto();
          }}
          onGalleryFileSelect={(file) => processSelectedFiles([file])}
          cameraLabel={t('videoGenerate.camera', 'Camera')}
          galleryLabel={t('videoGenerate.gallery', 'Gallery')}
          removeLabel={t('common.delete')}
          testIdPrefix="prompt"
          galleryInputTestId="prompt-gallery-direct-input"
          showHeader={false}
        />
      </BottomSheet>

      {/* Bottom panel — Figma 318:5101 (surface/1 lvl, padding 16/24, gap 12,
          topLeftRadius=16, topRightRadius=16, stroke border/control). */}
      <div
        className={[
          'app-create-panel fixed left-0 right-0 z-30 bg-[#010103] px-4 pt-4 pb-6 rounded-t-2xl',
          'transition-[bottom] duration-150 ease-out',
        ].join(' ')}
        style={{
          borderTop: '1px solid rgba(255,255,255,0.16)',
          bottom: promptCreatePanelBottom,
        }}
      >
        <CreateActionControls
          batchCount={batchCount}
          totalCredits={totalCredits}
          totalCreditsWord={totalCreditsWord}
          balanceValue={balanceValue}
          createLabel={t('prompt.create')}
          balanceLabel={t('prompt.balance')}
          generatingLabel={t('prompt.generating')}
          loading={isGenerating}
          onCreate={handleGenerate}
          onDecrease={() => {
            hapticFeedback('light');
            setBatchCount((c) => Math.max(1, c - 1));
          }}
          onIncrease={handleIncBatch}
        />
      </div>
    </div>
  );
}

export default Prompt;
